/* eslint-disable unicorn/consistent-function-scoping */
import { Flex } from '@huspy/briks-web';
import { useGetBanks } from '@modules/banks/hooks/queries/useGetBanks';
import briksTheme from '@shared/briks-theme';
import MultiSelect from '@components/v2/MultiSelect';
import { useEffect } from 'react';
import {
  CONTROL_KEYS, ControlKey, ControlPanelValuesType
} from '../types';
import {
  TYPE_OF_EMPLOYMENT, INTEREST_RATE_TYPE, CITIZEN_STATE, TRANSACTION_TYPE,
  LOAN_TO_VALUE_RATIO, CUST_SEGMENT_ACCOUNT_TYPE, IS_EXCLUSIVE, MORTGAGE_TYPE,
  VALIDITY, getFilterEnum,
  FIXED_RATE_YEARS
} from '../enums';
import { Tag } from '../components/Tag';
import { valueUnMapper } from '../mapper';
import ButtonsSelect from '../../../../../../../components/DropdownSelector/ButtonsSelect';

export const ControlPanel = ({ values, onChange }: { values: ControlPanelValuesType, onChange: Function }) => {
  const { data: bankList, isLoading: isBankListLoading } = useGetBanks();

  const handleOnSelect = (key: ControlKey, selectedValues: string[]) => {
    onChange(key, selectedValues);
  };

  const getValues = (key: ControlKey) => {
    if (!values) return [];
    return valueUnMapper(values[key]);
  };

  const rateTypeValues = getValues(CONTROL_KEYS.INTEREST_RATE_TYPE);

  useEffect(() => {
    if (rateTypeValues.length === 0 || !rateTypeValues.includes(INTEREST_RATE_TYPE.fixed.value)) {
      handleOnSelect(CONTROL_KEYS.FIXED_RATE_YEARS, []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateTypeValues.join(',')]);

  return (
    <Flex direction='column' gap='2'>
      <Flex gap='2' wrap='wrap'>
        {/* Banks */}
        { !isBankListLoading && bankList
        && (
          <MultiSelect
            label='Banks'
            header='Banks'
            data={ bankList.map(
              (item) => ({
                value: item.title,
                label: item.title,
              })
            ) }
            initialDataValues={ getValues(CONTROL_KEYS.BANK_NAME) }
            onApply={ (v) => handleOnSelect(CONTROL_KEYS.BANK_NAME, v) }
            dataTest='filter-banks'
            isActive={ getValues(CONTROL_KEYS.BANK_NAME).length > 0 }
            showAppliedData
            applyOnSelect
            showSearch
          />
        )}

        {/* Employment status */}
        <MultiSelect
          label='Employment status'
          header='Employment status'
          contentHeight='auto'
          data={ [
            TYPE_OF_EMPLOYMENT.salaried.value,
            TYPE_OF_EMPLOYMENT.selfEmploymed.value
          ].map(
            (item) => (getFilterEnum(TYPE_OF_EMPLOYMENT, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.TYPE_OF_EMPLOYMENT) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.TYPE_OF_EMPLOYMENT, v) }
          dataTest='filter-employment-status'
          isActive={ getValues(CONTROL_KEYS.TYPE_OF_EMPLOYMENT).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Residency status */}
        <MultiSelect
          label='Residency status'
          header='Residency status'
          contentHeight='auto'
          data={ [
            CITIZEN_STATE.uaeNational.value,
            CITIZEN_STATE.uaeResident.value,
            CITIZEN_STATE.nonResident.value
          ].map(
            (item) => (getFilterEnum(CITIZEN_STATE, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.CITIZEN_STATE) }
          onApply={ (v) => {
            if (v.includes(CITIZEN_STATE.uaeNational.value)) {
              handleOnSelect(CONTROL_KEYS.CITIZEN_STATE, [...v, CITIZEN_STATE.uaeResident.value]);
              return;
            }

            handleOnSelect(CONTROL_KEYS.CITIZEN_STATE, v);
          } }
          dataTest='filter-residency-status'
          isActive={ getValues(CONTROL_KEYS.CITIZEN_STATE).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Mortgage type */}
        <MultiSelect
          label='Mortgage type'
          header='Mortgage type'
          contentHeight='auto'
          data={ [
            MORTGAGE_TYPE.conventional.value,
            MORTGAGE_TYPE.islamic.value
          ].map(
            (item) => ({
              label: getFilterEnum(MORTGAGE_TYPE, item).label,
              value: getFilterEnum(MORTGAGE_TYPE, item).value,
              // eslint-disable-next-line react/no-unstable-nested-components
              node: () => (
                <Tag name={ item } />
              ),
            })
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.TYPE_OF_MORTGAGE) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.TYPE_OF_MORTGAGE, v) }
          dataTest='filter-mortgage-type'
          isActive={ getValues(CONTROL_KEYS.TYPE_OF_MORTGAGE).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Transaction type */}
        <MultiSelect
          label='Transaction type'
          header='Transaction type'
          contentHeight='auto'
          data={ [
            TRANSACTION_TYPE.primary.value,
            TRANSACTION_TYPE.buyoutAndEquity.value,
            TRANSACTION_TYPE.buyout.value,
            TRANSACTION_TYPE.equity.value,
            TRANSACTION_TYPE.underConstructionProperty.value,
            TRANSACTION_TYPE.handoverAndEquity.value
          ].map(
            (item) => ({
              label: getFilterEnum(TRANSACTION_TYPE, item).label,
              value: getFilterEnum(TRANSACTION_TYPE, item).value,
              // eslint-disable-next-line react/no-unstable-nested-components
              node: () => (
                <Tag name={ item } />
              ),
            })
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.TYPE_OF_TRANSACTION) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.TYPE_OF_TRANSACTION, v) }
          dataTest='filter-transaction-type'
          isActive={ getValues(CONTROL_KEYS.TYPE_OF_TRANSACTION).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Rate type */}
        <MultiSelect
          label='Rate'
          header='Rate'
          contentHeight='auto'
          data={ [
            INTEREST_RATE_TYPE.fixed.value,
            INTEREST_RATE_TYPE.variable.value
          ].map(
            (item) => (getFilterEnum(INTEREST_RATE_TYPE, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.INTEREST_RATE_TYPE) }
          onApply={ (v) => {
            handleOnSelect(CONTROL_KEYS.INTEREST_RATE_TYPE, v);
          } }
          dataTest='filter-rate-type'
          isActive={ getValues(CONTROL_KEYS.INTEREST_RATE_TYPE).length > 0 }
          doNotCloseWhenApply
          showAppliedData
          applyOnSelect
        >
          {getValues(CONTROL_KEYS.INTEREST_RATE_TYPE).includes(INTEREST_RATE_TYPE.fixed.value) ? (
            <Flex style={ { paddingTop: briksTheme.spacing[4] } }>
              <ButtonsSelect
                data={ FIXED_RATE_YEARS }
                onSelect={ (v) => handleOnSelect(CONTROL_KEYS.FIXED_RATE_YEARS, v) }
                value={ getValues(CONTROL_KEYS.FIXED_RATE_YEARS) }
              />
            </Flex>
          ) : undefined}
        </MultiSelect>

        {/* LTV */}
        <MultiSelect
          label='LTV'
          header='LTV'
          contentHeight='auto'
          data={ [
            LOAN_TO_VALUE_RATIO['60%'].value,
            LOAN_TO_VALUE_RATIO['65%'].value,
            LOAN_TO_VALUE_RATIO['70%'].value,
            LOAN_TO_VALUE_RATIO['75%'].value,
            LOAN_TO_VALUE_RATIO['80%'].value
          ].map(
            (item) => (getFilterEnum(LOAN_TO_VALUE_RATIO, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.LOAN_TO_VALUE_RATIO) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.LOAN_TO_VALUE_RATIO, v) }
          dataTest='filter-ltv'
          isActive={ getValues(CONTROL_KEYS.LOAN_TO_VALUE_RATIO).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Salary transfer */}
        <MultiSelect
          label='Salary transfer'
          header='Salary transfer'
          contentHeight='auto'
          data={ [
            CUST_SEGMENT_ACCOUNT_TYPE.STL.value,
            CUST_SEGMENT_ACCOUNT_TYPE.NSTL.value
          ].map(
            (item) => (getFilterEnum(CUST_SEGMENT_ACCOUNT_TYPE, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.CUST_SEGMENT_ACCOUNT_TYPE) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.CUST_SEGMENT_ACCOUNT_TYPE, v) }
          dataTest='filter-salary-transfer'
          isActive={ getValues(CONTROL_KEYS.CUST_SEGMENT_ACCOUNT_TYPE).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Exclusivity */}
        <MultiSelect
          label='Exclusivity'
          header='Exclusivity'
          contentHeight='auto'
          data={ [
            IS_EXCLUSIVE.exclusive.value,
            IS_EXCLUSIVE.notExclusive.value
          ].map(
            (item) => (getFilterEnum(IS_EXCLUSIVE, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.IS_EXCLUSIVE) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.IS_EXCLUSIVE, v) }
          dataTest='filter-exclusivity'
          isActive={ getValues(CONTROL_KEYS.IS_EXCLUSIVE).length > 0 }
          showAppliedData
          applyOnSelect
        />

        {/* Validity */}
        <MultiSelect
          label='Validity'
          header='Validity'
          contentHeight='auto'
          data={ [
            VALIDITY.active.value,
            VALIDITY.inactive.value
          ].map(
            (item) => (getFilterEnum(VALIDITY, item))
          ) }
          initialDataValues={ getValues(CONTROL_KEYS.STATUS_TYPE) }
          onApply={ (v) => handleOnSelect(CONTROL_KEYS.STATUS_TYPE, v) }
          dataTest='filter-validity'
          isActive={ getValues(CONTROL_KEYS.STATUS_TYPE).length > 0 }
          showAppliedData
          applyOnSelect
        />
      </Flex>
    </Flex>
  );
};
